const API_URL = process.env.API_URL || process.env.NEXT_PUBLIC_API_URL;

export interface RequestConfig<T = any> extends RequestInit {
  onResponse?: (response: Response) => T;
  body?: any;
  params?: Record<string, any>;
}

export interface CustomRequestPromise<T> extends Promise<T> {
  cancel: () => void;
}

export function CustomRequest<T>(
  urlConfig: URL | string,
  requestConfig: RequestConfig<T> = {}
): CustomRequestPromise<T> {
  const controller = new AbortController();
  const url = new URL(urlConfig, API_URL);

  if (requestConfig.params) {
    Object.entries(requestConfig.params).forEach(([key, val]) => {
      if (Array.isArray(val)) {
        val.forEach((item) => url.searchParams.append(key, item));
      } else {
        url.searchParams.set(key, val);
      }
    });
  }

  const promise = new Promise((resolve, reject) => {
    fetch(url, {
      ...requestConfig,
      headers: {
        Version: "2",
        Accept: "application/json",
        "Content-Type": "application/json",
        "Server-Secret": process.env.API_SECRET || "",
        "CF-Access-Client-Id": process.env.CLOUDFLARE_ACCESS_ID || "",
        "CF-Access-Client-Secret": process.env.CLOUDFLARE_ACCESS_SECRET || "",
        ...(requestConfig.headers || {}),
      },
      body: requestConfig.body ? JSON.stringify(requestConfig.body) : undefined,
      signal: controller.signal,
    })
      .then((response) => {
        if (requestConfig.onResponse) return requestConfig.onResponse(response);
        return response.json();
      })
      .then((data) => {
        if (data?.error) return reject(data.error);
        return resolve(data);
      })
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === "AbortError") return;
        reject(error);
      });
  }) as CustomRequestPromise<T>;

  promise.cancel = () => controller.abort();

  return promise;
}
